import React, { useState, useEffect, useCallback } from 'react';
import { AiOutlineSearch } from 'react-icons/ai';
import { useLocation, useNavigate } from 'react-router-dom';
import { normalizeImageUrl } from '../components/Post';
import './Explore.css';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import Post from '../components/Post';
import { CircularProgress } from '@mui/material';
import { apiService } from '../utils/api';
import config from '../config';
import { setPageTitle, setMetaTags } from '../utils/seoUtils';

function Explore() {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [trendingTopics, setTrendingTopics] = useState([]);
  
  // URLからクエリパラメータを取得するためのロケーションフック
  const location = useLocation();
  const navigate = useNavigate();
  const { isLoggedIn, currentUser, token } = useAuth();

  // タブの状態
  const [activeTab, setActiveTab] = useState('トップ');
  const tabs = ['トップ', 'ユーザー', '投稿', 'メディア', 'トレンド'];
  
  // ユーザーアクション状態（一元管理）
  const [userActions, setUserActions] = useState({
    liked_posts: [],
    bookmarked_posts: [],
    retweeted_posts: [],
    loading: false,
    loaded: false
  });

  // ユーザーアクション取得関数（一元化）
  const fetchUserActions = useCallback(async () => {
    if (!isLoggedIn || userActions.loading || userActions.loaded) {
      return;
    }

    try {
      setUserActions(prev => ({ ...prev, loading: true }));
      
      const token = localStorage.getItem('authToken');
      if (!token) {
        console.log('ユーザーアクション取得: トークンなし');
        return;
      }

      console.log('=== ユーザーアクション取得開始（Explore） ===');
      
      const response = await apiService.get('/api/posts/user-actions', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = response.data || { liked_posts: [], bookmarked_posts: [], retweeted_posts: [] };
      
      setUserActions({
        liked_posts: data.liked_posts || [],
        bookmarked_posts: data.bookmarked_posts || [],
        retweeted_posts: data.retweeted_posts || [],
        loading: false,
        loaded: true
      });
      
      console.log('ユーザーアクション取得完了（Explore）:', {
        liked: data.liked_posts?.length || 0,
        bookmarked: data.bookmarked_posts?.length || 0,
        retweeted: data.retweeted_posts?.length || 0
      });
      
      // 詳細なデバッグ情報を出力
      console.log('ユーザーアクション詳細（Explore）:', {
        liked_posts: data.liked_posts,
        bookmarked_posts: data.bookmarked_posts,
        retweeted_posts: data.retweeted_posts
      });
      
    } catch (error) {
      console.error('ユーザーアクション取得エラー（Explore）:', error);
      setUserActions({
        liked_posts: [],
        bookmarked_posts: [],
        retweeted_posts: [],
        loading: false,
        loaded: false
      });
    }
  }, [isLoggedIn, userActions.loading, userActions.loaded]);

  // エクスプローラーページのSEO設定
  useEffect(() => {
    setPageTitle('探索');
    setMetaTags({
      title: '探索 | デリバリーナビ',
      description: 'トレンドのハッシュタグ、人気の投稿、おすすめユーザーを探索しよう！新しい出会いや興味深いコンテンツを発見できます。',
      keywords: '探索, 検索, ハッシュタグ, トレンド, 投稿, ユーザー, 発見',
      type: 'website'
    });
  }, []);

  // 初期ロード時にトレンドを取得
  useEffect(() => {
    fetchTrendingTopics();
  }, []);

  // ユーザーアクション取得を初期化時に実行
  useEffect(() => {
    if (isLoggedIn && !userActions.loaded && !userActions.loading) {
      fetchUserActions();
    }
  }, [isLoggedIn, fetchUserActions, userActions.loaded, userActions.loading]);

  // いいね処理ハンドラー
  const handleLikePost = useCallback(async (postId) => {
    if (!isLoggedIn) {
      navigate('/login');
      return;
    }

    try {
      console.log(`Explore.js: いいね処理開始 - 投稿${postId}`);
      
      // ユーザーアクション状態を更新
      setUserActions(prev => {
        const isCurrentlyLiked = prev.liked_posts.some(id => Number(id) === Number(postId));
        const newLikedPosts = isCurrentlyLiked
          ? prev.liked_posts.filter(id => Number(id) !== Number(postId))
          : [...prev.liked_posts, postId];
        
        return {
          ...prev,
          liked_posts: newLikedPosts
        };
      });
      
      console.log(`Explore.js: いいね処理完了 - 投稿${postId}`);
    } catch (error) {
      console.error('Explore.js: いいね処理エラー:', error);
    }
  }, [isLoggedIn, navigate]);

  // リツイート処理ハンドラー
  const handleRetweetPost = useCallback(async (postId) => {
    if (!isLoggedIn) {
      navigate('/login');
      return;
    }

    try {
      console.log(`Explore.js: リツイート処理開始 - 投稿${postId}`);
      
      // ユーザーアクション状態を更新
      setUserActions(prev => {
        const isCurrentlyRetweeted = prev.retweeted_posts.some(id => Number(id) === Number(postId));
        const newRetweetedPosts = isCurrentlyRetweeted
          ? prev.retweeted_posts.filter(id => Number(id) !== Number(postId))
          : [...prev.retweeted_posts, postId];
        
        return {
          ...prev,
          retweeted_posts: newRetweetedPosts
        };
      });
      
      console.log(`Explore.js: リツイート処理完了 - 投稿${postId}`);
    } catch (error) {
      console.error('Explore.js: リツイート処理エラー:', error);
    }
  }, [isLoggedIn, navigate]);

  // コメント処理ハンドラー
  const handleCommentPost = useCallback((postId) => {
    console.log(`Explore.js: コメント処理 - 投稿${postId}`);
    navigate(`/post/${postId}`);
  }, [navigate]);

  // 投稿削除処理ハンドラー
  const handleDeletePost = useCallback(async (postId) => {
    console.log(`Explore.js: 投稿削除処理 - 投稿${postId}`);
    // 検索結果から削除された投稿を除外
    setSearchResults(prev => prev.filter(result => 
      result.type !== 'post' || result.postData.id !== postId
    ));
  }, []);

  // ブックマーク保存処理ハンドラー
  const handleSavePost = useCallback(async (postId) => {
    if (!isLoggedIn) {
      navigate('/login');
      return;
    }

    try {
      console.log(`Explore.js: ブックマーク保存処理開始 - 投稿${postId}`);
      
      // ユーザーアクション状態を更新
      setUserActions(prev => ({
        ...prev,
        bookmarked_posts: [...prev.bookmarked_posts, postId]
      }));
      
      console.log(`Explore.js: ブックマーク保存処理完了 - 投稿${postId}`);
    } catch (error) {
      console.error('Explore.js: ブックマーク保存処理エラー:', error);
    }
  }, [isLoggedIn, navigate]);

  // ブックマーク削除処理ハンドラー
  const handleRemoveSavedPost = useCallback(async (postId) => {
    if (!isLoggedIn) {
      navigate('/login');
      return;
    }

    try {
      console.log(`Explore.js: ブックマーク削除処理開始 - 投稿${postId}`);
      
      // ユーザーアクション状態を更新
      setUserActions(prev => ({
        ...prev,
        bookmarked_posts: prev.bookmarked_posts.filter(id => Number(id) !== Number(postId))
      }));
      
      console.log(`Explore.js: ブックマーク削除処理完了 - 投稿${postId}`);
    } catch (error) {
      console.error('Explore.js: ブックマーク削除処理エラー:', error);
    }
  }, [isLoggedIn, navigate]);

  // トレンドハッシュタグを取得する関数
  const fetchTrendingTopics = async () => {
    try {
      
      const response = await apiService.get('/api/stats/overview', {
        // JSONレスポンスを期待することを明示
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        // キャッシュの回避（デバッグ用）
        params: {
          _t: Date.now()
        }
      });
      
      
      
      if (response.data && response.data.data && response.data.data.trending && response.data.data.trending.popular_hashtags) {
        
        setTrendingTopics(response.data.data.trending.popular_hashtags.map(hashtag => ({
          id: `api_${hashtag.id}`,  // APIから取得したIDに固有プレフィックスを追加
          name: `#${hashtag.tag}`,
          count: `${hashtag.count} 投稿`,
          view_count: hashtag.view_count || 0,
          view_text: `${hashtag.view_count ? hashtag.view_count.toLocaleString() : 0} ビュー`,
          category: 'トレンド'
        })));
      } else {
        
      }
    } catch (error) {
      
      
      // API接続エラーのメッセージを詳細化
      const errorMessage = error?.response?.status 
        ? `APIエラー: ${error.response.status}` 
        : error.message || 'API接続エラー';
      
      
      // エラーの詳細なデバッグ情報
      if (error.response) {
        
        
        if (typeof error.response.data === 'string' && error.response.data.length < 200) {
          
        }
      }
      
      // APIが失敗した場合、別のエンドポイントを試みる
      try {
        
        const fallbackResponse = await apiService.get('/api/posts/hashtags/views', {
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          },
          params: {
            _t: Date.now()  // キャッシュの回避
          }
        });
        if (fallbackResponse.data && fallbackResponse.data.hashtags) {
          setTrendingTopics(fallbackResponse.data.hashtags.map(hashtag => ({
            id: `fallback_${hashtag.id}`,  // フォールバックAPIから取得したIDに固有プレフィックスを追加
            name: `#${hashtag.tag}`,
            count: `${hashtag.post_count} 投稿`,
            view_count: hashtag.view_count || 0,
            view_text: `${hashtag.view_count ? hashtag.view_count.toLocaleString() : 0} ビュー`,
            category: 'トレンド'
          })));
          return;
        }
      } catch (fallbackError) {
        
      }
      
      // フォールバックとしてダミーデータを使用
      setTrendingTopics([
        { id: 'mock_1', name: '#新商品発売', count: '1.2万 投稿', view_text: '15.6万 ビュー', view_count: 156000, category: 'ビジネス' },
        { id: 'mock_2', name: '#今日の天気', count: '8,542 投稿', view_text: '10.2万 ビュー', view_count: 102000, category: '天気' },
        { id: 'mock_3', name: '#スポーツニュース', count: '5,283 投稿', view_text: '7.5万 ビュー', view_count: 75000, category: 'スポーツ' },
        { id: 'mock_4', name: '#新曲発表', count: '3,210 投稿', view_text: '5.8万 ビュー', view_count: 58000, category: 'エンタメ' },
        { id: 'mock_5', name: '#テクノロジー', count: '2,854 投稿', view_text: '4.3万 ビュー', view_count: 43000, category: 'テクノロジー' }
      ]);
    }
  };

  // URLからハッシュタグパラメータを取得し、検索を実行
  useEffect(() => {
    
    const searchParams = new URLSearchParams(location.search);
    const hashtagParam = searchParams.get('hashtag');
    const tokenParam = searchParams.get('token'); // URLからトークンを取得
    
    // トークンが含まれていれば、localStorageに一時的に保存
    if (tokenParam) {
      localStorage.setItem('temp_token', tokenParam);
    }
    
    if (hashtagParam) {
      // ハッシュタグが指定されていれば、検索クエリにセットして検索を実行
      const searchTerm = `#${hashtagParam}`;
      
      setSearchQuery(searchTerm);
      executeSearch(searchTerm);
      // 投稿タブをアクティブにする
      setActiveTab('投稿');
    }
  }, [location.search, location.pathname]);

  // Post内のハッシュタグがクリックされた時のハンドラ（コンポーネントの外部からアクセス用）
  useEffect(() => {
    // カスタムイベント「hashtagClick」のリスナーを登録
    const handleHashtagClickEvent = (event) => {
      const { hashtag } = event.detail;
      if (hashtag) {
        
        const searchTerm = `#${hashtag}`;
        setSearchQuery(searchTerm);
        executeSearch(searchTerm);
        setActiveTab('投稿');
      }
    };
    
    // URLをチェックして、ハッシュタグパラメータがあるか確認
    const checkHashtagParam = () => {
      const searchParams = new URLSearchParams(window.location.search);
      const hashtagParam = searchParams.get('hashtag');
      
      if (hashtagParam) {
        const searchTerm = `#${hashtagParam}`;
        setSearchQuery(searchTerm);
        executeSearch(searchTerm);
        setActiveTab('投稿');
      }
    };

    // イベントリスナーを登録
    window.addEventListener('popstate', checkHashtagParam);
    window.addEventListener('hashtagClick', handleHashtagClickEvent);
    
    // クリーンアップ関数
    return () => {
      window.removeEventListener('popstate', checkHashtagParam);
      window.removeEventListener('hashtagClick', handleHashtagClickEvent);
    };
  }, []);

  // 検索を実行する関数
  const executeSearch = async (query) => {
    if (!query.trim()) return;

    setLoading(true);
    setSearchResults([]);
    
    try {
      let results = [];
      
      // ハッシュタグ検索の場合
      if (query.startsWith('#')) {
        const hashtag = query.substring(1); // '#'を除去
        if (activeTab === '投稿' || activeTab === 'トップ') {
          try {
            // apiServiceを使用して統一的なトークン管理とエラーハンドリングを適用
            const postsResponse = await apiService.get(`/api/posts/hashtag/${hashtag}`, {
              params: {
                _t: Date.now() // キャッシュバスター
              }
            });
            
            if (postsResponse.data && postsResponse.data.posts) {
              // 受信データをログ出力
              
              
              // リツイートの重複を除外するために投稿IDベースでフィルタリング
              const uniquePosts = {};
              postsResponse.data.posts.forEach(post => {
                // オリジナル投稿を優先して保持
                if (!uniquePosts[post.original_id || post.id] || post.original_id === undefined) {
                  uniquePosts[post.original_id || post.id] = post;
                }
              });
              
              // オブジェクトから配列に変換
              const filteredPosts = Object.values(uniquePosts);
              
              
              results = filteredPosts.map(post => ({
                id: `post_${post.id}`,
                type: 'post',
                postData: post
              }));
            }
          } catch (error) {
            
            // エラー発生時に通常検索へフォールバック
            if (query.length >= 2) {
              // 通常検索で代用
              const postsResponse = await apiService.get('/api/posts', {
                headers: {
                  'Accept': 'application/json',
                  'Content-Type': 'application/json'
                },
                params: {
                  _t: Date.now() // キャッシュバスター
                }
              });
              
              if (postsResponse.data && postsResponse.data.posts) {
                
                
                // ハッシュタグ付きの投稿のみを検索（「#ハッシュタグ」の形式で検索）
                const hashtagRegex = new RegExp(`#${hashtag.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i');
                const textFilteredPosts = postsResponse.data.posts.filter(
                  post => post.text && hashtagRegex.test(post.text)
                );
                
                // リツイートの重複を除外するために投稿IDベースでフィルタリング
                const uniquePosts = {};
                textFilteredPosts.forEach(post => {
                  // オリジナル投稿を優先して保持
                  if (!uniquePosts[post.original_id || post.id] || post.original_id === undefined) {
                    uniquePosts[post.original_id || post.id] = post;
                  }
                });
                
                // オブジェクトから配列に変換
                const filteredPosts = Object.values(uniquePosts);
                
                results = filteredPosts.map(post => ({
                  id: `post_${post.id}`,
                  type: 'post',
                  postData: post
                }));
              }
            }
          }
        } else if (activeTab === 'メディア') {
          try {
            // apiServiceを使用して統一的なトークン管理とエラーハンドリングを適用
            const postsResponse = await apiService.get(`/api/posts/hashtag/${hashtag}`, {
              params: {
                _t: Date.now() // キャッシュバスター
              }
            });
            
            if (postsResponse.data && postsResponse.data.posts) {
              // メディア投稿のみをフィルタリング
              const mediaFilteredPosts = postsResponse.data.posts.filter(post => post.image || post.media);
              
              // リツイートの重複を除外するために投稿IDベースでフィルタリング
              const uniquePosts = {};
              mediaFilteredPosts.forEach(post => {
                // オリジナル投稿を優先して保持
                if (!uniquePosts[post.original_id || post.id] || post.original_id === undefined) {
                  uniquePosts[post.original_id || post.id] = post;
                }
              });
              
              // オブジェクトから配列に変換
              const filteredPosts = Object.values(uniquePosts);
              
              results = filteredPosts.map(post => ({
                id: `post_${post.id}`,
                type: 'post',
                postData: post
              }));
            }
          } catch (error) {
            
          }
        }
      } else {
        // 通常検索
        if (activeTab === 'ユーザー' || activeTab === 'トップ') {
          // ユーザー検索
          try {
            const usersResponse = await apiService.get(`/api/social/search/users?q=${query}`, {
              headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
              }
            });
            
            if (usersResponse.data && usersResponse.data.users) {
              const userResults = usersResponse.data.users.map(user => {
                // アバターURLをフルパスに変換
                let avatarUrl = user.avatar;
                if (avatarUrl && avatarUrl.startsWith('/static/')) {
                  avatarUrl = `${config.API_BASE_URL.replace(/\/$/, '')}${avatarUrl}`;
                }
                
                return {
                  id: `user_${user.id}`,  // ユーザーIDにプレフィックスを追加
                  type: 'user',
                  name: user.display_name,
                  username: user.username,
                  avatar: avatarUrl || 'https://picsum.photos/id/1001/48/48',
                  verified: user.verified || false,
                  is_following: user.is_following || false
                };
              });
              
              if (activeTab === 'ユーザー') {
                results = userResults;
              } else {
                // トップタブの場合は結果を混合
                results = [...results, ...userResults].slice(0, 10);
              }
            }
          } catch (error) {
            
          }
        }
        
        if (activeTab === '投稿' || activeTab === 'トップ' || activeTab === 'メディア') {
          // 投稿検索（キーワード検索はAPIがないため、全投稿を取得して検索）
          try {
            
            const postsResponse = await apiService.get('/api/posts', {
              headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
              },
              params: {
                _t: Date.now() // キャッシュバスター
              }
            });
            
            
            
            if (postsResponse.data && postsResponse.data.posts) {
              // 最初の数件の投稿データを確認（デバッグ用）
              
              
              // テキスト内容で検索
              const textFilteredPosts = postsResponse.data.posts.filter(
                post => post.text && post.text.toLowerCase().includes(query.toLowerCase())
              );
              
              
              
              // メディアタブの場合は画像付き投稿のみ
              let filteredPosts = textFilteredPosts;
              if (activeTab === 'メディア') {
                filteredPosts = filteredPosts.filter(post => post.image || post.media);
                
              }
              
              // リツイートの重複を除外するために投稿IDベースでフィルタリング
              const uniquePosts = {};
              filteredPosts.forEach(post => {
                // オリジナル投稿を優先して保持
                if (!uniquePosts[post.original_id || post.id] || post.original_id === undefined) {
                  uniquePosts[post.original_id || post.id] = post;
                }
              });
              
              // オブジェクトから配列に変換
              const uniqueFilteredPosts = Object.values(uniquePosts);
              
              
              const postResults = uniqueFilteredPosts.map(post => ({
                id: `post_${post.id}`,
                type: 'post',
                postData: post
              }));
              
              if (activeTab === '投稿' || activeTab === 'メディア') {
                results = postResults;
              } else if (activeTab === 'トップ') {
                // トップタブの場合は結果を混合（最大10件）
                results = [...results, ...postResults].slice(0, 10);
              }
            }
          } catch (error) {
            
          }
        }
        
        // トレンド検索タブ
        if (activeTab === 'トレンド') {
          try {
            const hashtagsResponse = await apiService.get('/api/posts/hashtags/views', {
              headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
              },
              params: {
                _t: Date.now() // キャッシュバスター
              }
            });
            
            // ... existing code ...
          } catch (error) {
            
          }
        }
      }
      
      
      setSearchResults(results);
      
      // 検索結果のSEO設定
      if (query) {
        setPageTitle(`"${query}" の検索結果`);
        setMetaTags({
          title: `"${query}" の検索結果 | デリバリーナビ`,
          description: `"${query}" に関する投稿、ユーザー、ハッシュタグの検索結果です。`,
          keywords: `${query}, 検索, 結果, 投稿, ユーザー`,
          type: 'website'
        });
      }
    } catch (error) {
      
    } finally {
      setLoading(false);
    }
  };

  // タブ切り替え時に検索を再実行
  useEffect(() => {
    if (searchQuery.trim()) {
      executeSearch(searchQuery);
    }
  }, [activeTab]);

  // トレンドをクリックした時のハンドラ
  const handleTrendClick = (trend) => {
    // '#'から始まる場合は、そのまま検索クエリにセット
    setSearchQuery(trend);
    
    // クエリパラメータをURLに反映
    const hashtag = trend.substring(1); // '#'を除去
    
    // 投稿タブをアクティブにする
    setActiveTab('投稿');
    
    // URLを変更してから検索を実行（URLの変更でuseEffectが発火するため）
    navigate(`/explore?hashtag=${hashtag}`);
  };

  // 検索機能
  const handleSearch = (e) => {
    e.preventDefault();
    
    // クエリパラメータをURLに反映
    if (searchQuery.startsWith('#')) {
      const hashtag = searchQuery.substring(1); // '#'を除去
      navigate(`/explore?hashtag=${hashtag}`);
    } else {
      // 通常の検索はURLパラメータなしで、検索を直接実行
      navigate('/explore');
      executeSearch(searchQuery);
    }
  };

  // 入力中に検索を実行する関数
  const handleInputChange = (e) => {
    const newQuery = e.target.value;
    setSearchQuery(newQuery);
    
    // 入力が3文字以上になったら検索を実行
    if (newQuery.length >= 3) {
      executeSearch(newQuery);
    } else if (newQuery.length === 0) {
      // 入力がクリアされた場合は検索結果もクリア
      setSearchResults([]);
    }
  };

  return (
    <div className="explore">
      <div className="explore__header">
        <form onSubmit={handleSearch} className="explore__searchContainer">
          <AiOutlineSearch className="explore__searchIcon" />
          <input
            className="explore__searchInput"
            type="text"
            placeholder="検索"
            value={searchQuery}
            onChange={handleInputChange}
          />
          <button type="submit" className="explore__searchButton">検索</button>
        </form>
      </div>

      <div className="explore__tabs">
        {tabs.map(tab => (
          <button
            key={tab}
            className={`explore__tab ${activeTab === tab ? 'active' : ''}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab}
          </button>
        ))}
      </div>

      <div className="explore__content">
        {loading ? (
          <div className="explore__loading">
            <CircularProgress size={40} />
            <p>検索中...</p>
          </div>
        ) : activeTab === 'トレンド' ? (
          // トレンドタブの表示
          <div className="explore__trending">
            <h2>トレンド</h2>
            <div className="explore__trendsList">
              {trendingTopics.map((trend, index) => (
                <div 
                  key={`${trend.id}_${index}`} 
                  className="explore__trendItem"
                  onClick={() => handleTrendClick(trend.name)}
                >
                  <p className="explore__trendCategory">{trend.category === 'トレンド' ? '' : trend.category}</p>
                  <h3 className="explore__trendName">{trend.name}</h3>
                  <p className="explore__trendCount">{trend.count}</p>
                </div>
              ))}
            </div>
          </div>
        ) : searchQuery ? (
          // 検索結果表示（「トップ」「ユーザー」「投稿」「メディア」タブ）
          <div className="explore__results">
            <h2>「{searchQuery}」の検索結果</h2>
            
            {searchResults.length === 0 ? (
              <div className="explore__noResults">
                検索結果がありません。別のキーワードをお試しください。
              </div>
            ) : (
              <div className="explore__resultsList">
                {searchResults.map(result => (
                  <div key={result.id} className={`explore__resultItem explore__resultItem--${result.type}`}>
                    {result.type === 'user' && (
                      <div className="explore__user">
                                                  <img src={normalizeImageUrl(result.avatar)} alt={result.name} className="explore__avatar" 
                          onClick={() => navigate(`/profile/${result.username}`)}
                          style={{ cursor: 'pointer' }}
                        />
                        <div className="explore__userInfo" onClick={() => navigate(`/profile/${result.username}`)} style={{ cursor: 'pointer' }}>
                          <h4>{result.name}</h4>
                          <p>@{result.username}</p>
                        </div>
                        <button 
                          className={`explore__followButton ${result.is_following ? 'explore__followButton--following' : ''}`}
                          onClick={async () => {
                            try {
                              const endpoint = result.is_following ? 
                                `/api/social/unfollow/${result.username}` : 
                                `/api/social/follow/${result.username}`;
                              const method = result.is_following ? 'delete' : 'post';
                              
                              await apiService[method](endpoint, {}, {
                                headers: {
                                  'Authorization': `Bearer ${localStorage.getItem('token')}`,
                                  'Accept': 'application/json',
                                  'Content-Type': 'application/json'
                                }
                              });
                              
                              // 状態を更新
                              setSearchResults(searchResults.map(item => {
                                if (item.id === result.id) {
                                  return { ...item, is_following: !result.is_following };
                                }
                                return item;
                              }));
                            } catch (error) {
                              
                            }
                          }}
                        >
                          {result.is_following ? 'フォロー中' : 'フォロー'}
                        </button>
                      </div>
                    )}
                    
                    {result.type === 'post' && (
                      <div className="explore__post">
                        <Post
                          id={result.postData.id}
                          displayName={result.postData.author?.display_name || ''}
                          username={result.postData.author?.username || ''}
                          verified={result.postData.author?.verified || false}
                          text={result.postData.text || result.postData.content}
                          avatar={result.postData.author?.avatar || ''}
                          image={result.postData.image || result.postData.media}
                          createdAt={result.postData.created_at}
                          likes={result.postData.likes_count || result.postData.like_count || 0}
                          comments={result.postData.comments_count || result.postData.comment_count || 0}
                          retweets={result.postData.reposts_count || 0}
                          authorId={result.postData.author?.id || result.postData.user_id}
                          // ユーザーアクション情報を追加（数値で統一して判定）
                          liked={userActions.liked_posts.some(id => Number(id) === Number(result.postData.id))}
                          bookmarked={userActions.bookmarked_posts.some(id => Number(id) === Number(result.postData.id))}
                          retweeted={userActions.retweeted_posts.some(id => Number(id) === Number(result.postData.id))}
                          userActionsLoaded={userActions.loaded}
                          onLike={handleLikePost}
                          onRetweet={handleRetweetPost}
                          onComment={handleCommentPost}
                          onDelete={handleDeletePost}
                          onSave={handleSavePost}
                          onRemoveSave={handleRemoveSavedPost}
                        />
                      </div>
                    )}
                    
                    {result.type === 'trend' && (
                      <div className="explore__trend">
                        <div className="explore__trendInfo">
                          <h4>{result.name}</h4>
                          <p>{result.count}</p>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          // 検索前の表示（「トップ」「ユーザー」「投稿」「メディア」タブ）
          <div className="explore__emptyState">
            {activeTab === 'ユーザー' && (
              <div className="explore__emptyTab">
                <h3>ユーザーを検索</h3>
                <p>ユーザー名やプロフィール名で検索できます</p>
              </div>
            )}
            
            {activeTab === '投稿' && (
              <div className="explore__emptyTab">
                <h3>投稿を検索</h3>
                <p>キーワードやハッシュタグで投稿を検索できます</p>
              </div>
            )}
            
            {activeTab === 'メディア' && (
              <div className="explore__emptyTab">
                <h3>メディアを検索</h3>
                <p>画像付きの投稿を検索できます</p>
              </div>
            )}
            
            {activeTab === 'トップ' && (
              <div className="explore__trending">
                <h2>トレンド</h2>
                <div className="explore__trendsList">
                  {trendingTopics.map((trend, index) => (
                    <div 
                      key={`default_${trend.id}_${index}`} 
                      className="explore__trendItem"
                      onClick={() => handleTrendClick(trend.name)}
                    >
                      <p className="explore__trendCategory">{trend.category === 'トレンド' ? '' : trend.category}</p>
                      <h3 className="explore__trendName">{trend.name}</h3>
                      <p className="explore__trendCount">{trend.count}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default Explore; 